import type { IHostToolDefinition } from '../gpthelp-host-contract';
import type { ICardFormat, ICardTranslator } from '../cards/card-text';
import type { IAssistantNavTarget } from '@/domain/assistant/assistant-nav-targets';
import type { INavGroup } from '@/domain/layout/app-nav';

export class HostToolError extends Error {
  public readonly code: string;
  public readonly details?: unknown;

  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

/** The admin's vue-i18n: card text is written in its current UI locale. */
export interface IGpthelpHostI18n extends ICardTranslator {
  locale: string;
}

export interface ICartadigitalAdminHostToolDeps {
  restaurantStore: ReturnType<
    typeof import('@/stores/restaurant.store').useRestaurantStore
  >;
  authStore: ReturnType<typeof import('@/composition/use-auth').useAuthStore>;
  menuStore: ReturnType<typeof import('@/composition/use-menu').useMenuStore>;
  /** The «Asistente» panel store: writes config, pending proposals, highlight. */
  assistantStore: ReturnType<
    typeof import('@/composition/assistant/use-assistant-store').useAssistantStore
  >;
  i18n: IGpthelpHostI18n;
  /** The store's drawer navigation for the current member (role filter applied). */
  restaurantNav(): INavGroup[];
  /** The current route's path, without the app base («/bobas/menu»). */
  routePath(): string;
}

/** Availability gate built by `buildGpthelpToolGate()` (tool-gate.ts). */
export interface IGpthelpToolGate {
  hasRestaurantScope: boolean;
  canViewStats: boolean;
  canManageCaja: boolean;
  /** OrdersHistoric || OrdersManage — the order-history dual gate. */
  canViewOrdersHistory: boolean;
  canManageCatalog: boolean;
  /** CatalogManage || CatalogDisable: may turn products on and off. */
  canDisableCatalog: boolean;
  /** May turn products on and off in every branch at once (`catalogAccess`). */
  canHideEverywhere: boolean;
  canManageClub: boolean;
  canManageReservations: boolean;
  canTopupClub: boolean;
  /** OrdersManage — byte-identical to the Customers page nav/route gate. */
  canViewCustomers: boolean;
  canManageStore: boolean;
  /** UsersManage — the team page's permission (it also needs Plus). */
  canManageUsers: boolean;
  /** BranchSettings — the «Sucursales» page's permission (it also needs Pro). */
  canManageBranches: boolean;
  /**
   * The store settings page sends each branch's ordering, hours and delivery
   * to «Sucursales»: Pro with more than one active branch the member works in
   * (`multipleBranches`, RestaurantSettings.vue).
   */
  settingsPerBranch: boolean;
  /** currentRole.allCajas — gates day-close surfaces like the nav does. */
  allCajas: boolean;
  branchUsesCajas: boolean;
  /** The store's effective plan tier (Plus = 1, Pro = 2). */
  effectiveTier: number;
  /** The store has several menus (the menu uploader does not support them yet). */
  multipleMenus: boolean;
  /** Ops the assistant may prepare here (`GET assistant/config`), without those whose write tool is switched off; empty while writes are off. */
  assistantOps: string[];
  /** `GET assistant/config` of this store and branch has writes on; false while it is unknown. */
  assistantWritesEnabled: boolean;
  /** Ops switched off for this member (kill switch or a dark launch); empty while the config is unknown. */
  assistantDisabledOps: string[];
  /** Host tools switched off for this member; null while the config of this store and branch is unknown. */
  assistantDisabledTools: string[] | null;
  /** The support link (https or mailto:) of the config of this store and branch, writes on or off; null while unknown or unset. */
  supportUrl: string | null;
  /** Pages the assistant can take this member to. */
  navTargets: IAssistantNavTarget[];
}

/** Restaurant/branch scope resolved from the stores at execute time. */
export interface IGpthelpToolScope {
  restaurantId: number;
  branchId: number;
  timeZone: string;
  branchOrdersMode: string | null;
  branchUsesCajas: boolean;
}

/**
 * The widget's turn that asked for the tool (host contract §3.2). Empty
 * strings when an older widget did not send them.
 */
export interface IGpthelpToolTurn {
  conversationId: string;
  turnId: string;
  requestId: string;
  /** An earlier tool result of the conversation held customer-written text. */
  tainted: boolean;
  /** The user message that started the turn (≤500 characters). */
  userText: string;
}

export interface IGpthelpToolExecCtx {
  deps: ICartadigitalAdminHostToolDeps;
  scope: IGpthelpToolScope;
  /** The gate the tool was just re-checked with. */
  gate: IGpthelpToolGate;
  turn: IGpthelpToolTurn;
  /** Writes card text in the admin locale, with the store's money and the branch's time zone. */
  fmt: ICardFormat;
}

/**
 * What a tool does for the user (host-only, never sent to GPTHelp): `read`
 * answers with store data, `proposal` prepares an inert menu or inventory
 * change the user applies from a card, `ui` returns a card that takes the
 * user somewhere.
 */
export type IGpthelpToolPolicy = 'read' | 'proposal' | 'ui';

export interface IGpthelpTool {
  definition: IHostToolDefinition;
  /**
   * The definition offered for a gate, when it depends on it (e.g. an enum
   * of the pages this member can open). Keeps `definition.name`.
   */
  definitionFor?: (gate: IGpthelpToolGate) => IHostToolDefinition;
  policy: IGpthelpToolPolicy;
  /**
   * Host-only: the result can carry text written by customers (names, notes,
   * addresses), wrapped as `{ untrusted }` by sanitize.ts.
   */
  readsDinerText?: true;
  isAvailable: (gate: IGpthelpToolGate) => boolean;
  /**
   * Runs the tool. Each tool re-asserts its own permission gate here (throwing
   * HostToolError FORBIDDEN/NOT_AVAILABLE) so a direct execute on a gated-off
   * tool fails even if the definition was never offered.
   */
  execute: (
    args: Record<string, unknown>,
    ctx: IGpthelpToolExecCtx
  ) => Promise<unknown> | unknown;
}
